import type { ColorOption } from '@ai-color-modules/simulation/ColorPalette/colorOptions'

import { AREA_NAME_MAP, BASE_AREAS } from './processParameters'

/** 「すべての色」を表す UI 一時状態のセンチネル（永続化しない）. */
export const ALL_COLORS_SENTINEL = 'all' as const

/** 共有リンク保存時に「すべての色」を表す proto 側のセンチネル. */
export const ALL_COLORS_SHARE_SENTINEL = '__ALL__' as const

/** UI で扱うプリセット（色 id → ColorOption 変換済み）. */
export interface AreaColorPreset {
  /** preset ULID */
  id: string
  name: string
  /** display_order 昇順で並んだ色一覧 */
  colors: ColorOption[]
}

/**
 * editingArea（日本語ベースエリア名 or 'subarea-<idx>'）を
 * サーバ側の area_key / preset default キー体系へ変換する.
 *
 * - ベースエリア: 日本語名 → canonical 英語名（"outer wall and balcony" 等）
 * - カスタムエリア: 'subarea-<idx>' → '<base_image_id>:<idx>'
 *   （施主側の SharedAreaColors.area_key と対応。カット（外観カット=元画像）ごとに
 *   カスタムエリアの index が独立しているため、baseImageId で名前空間を分ける）
 *   baseImageId 未指定時は従来どおり '<idx>'（企業側の共有リンク作成キー体系）
 * - 変換できない場合は元の値をそのまま返す
 */
export const editingAreaToAreaKey = (
  editingArea: string,
  baseImageId?: bigint | number,
): string => {
  if (editingArea.startsWith('subarea-')) {
    const index = editingArea.replace('subarea-', '')
    return baseImageId === undefined ? index : `${baseImageId}:${index}`
  }
  return AREA_NAME_MAP[editingArea] ?? editingArea
}

/** editingArea がカスタムエリア（'subarea-<idx>'）かどうか. */
export const isCustomEditingArea = (editingArea: string): boolean =>
  editingArea.startsWith('subarea-')

/**
 * エリアごとの初期プリセット選択を解決する.
 * - ベースエリア: そのエリアのデフォルトプリセット（baseAreaDefaultPresetIds、canonical英語名キー）. 無ければ 'all'
 * - カスタムエリア: 常に 'all'（すべての色）
 */
export const resolveInitialPresetForArea = (
  editingArea: string,
  baseAreaDefaultPresetIds: Record<string, string> | undefined,
): string | typeof ALL_COLORS_SENTINEL => {
  if (isCustomEditingArea(editingArea)) {
    return ALL_COLORS_SENTINEL
  }
  const canonical = AREA_NAME_MAP[editingArea] ?? editingArea
  return baseAreaDefaultPresetIds?.[canonical] ?? ALL_COLORS_SENTINEL
}

/**
 * エリアの「実効プリセット選択」を導出する（コピー state を持たず毎回導出する）.
 *
 * - ユーザーの明示選択（`userSelectedPresetByArea`）があればそれ、
 *   無ければベースエリアのデフォルト（カスタムエリアは 'all'）
 * - 実在しない preset ID（削除済み / presets 未ロード）は 'all' に正規化する
 *   → Select の表示値（value が空文字だと placeholder すら出ない）と
 *     `resolveDialogColors` の結果が乖離し得ないことを保証する
 *
 * 「初期値を state にコピーする useEffect」を置かないこと。デフォルト（API）が後から
 * 届くケースで 'all' が確定し、そのエリアに永久にデフォルトが適用されなくなる.
 */
export const resolveEffectivePresetId = (
  editingArea: string,
  userSelectedPresetByArea: Record<string, string | typeof ALL_COLORS_SENTINEL>,
  baseAreaDefaultPresetIds: Record<string, string> | undefined,
  presets: AreaColorPreset[] | undefined,
): string | typeof ALL_COLORS_SENTINEL => {
  const candidate =
    userSelectedPresetByArea[editingArea] ??
    resolveInitialPresetForArea(editingArea, baseAreaDefaultPresetIds)
  if (candidate === ALL_COLORS_SENTINEL) {
    return ALL_COLORS_SENTINEL
  }
  return presets?.some(preset => preset.id === candidate) ? candidate : ALL_COLORS_SENTINEL
}

/**
 * 選択中のプリセットに応じて、色指定ダイアログに渡す色一覧を解決する.
 * - 'all' / 未選択 / 該当プリセット無し → availableColors（全色）
 * - preset 選択時 → そのプリセットの色（display_order 順は presets 側で解決済み）
 */
export const resolveDialogColors = (
  selectedPresetId: string | typeof ALL_COLORS_SENTINEL | undefined,
  presets: AreaColorPreset[] | undefined,
  availableColors: ColorOption[],
): ColorOption[] => {
  if (!selectedPresetId || selectedPresetId === ALL_COLORS_SENTINEL) {
    return availableColors
  }
  const preset = presets?.find(p => p.id === selectedPresetId)
  if (!preset) {
    return availableColors
  }
  return preset.colors
}

/**
 * 共有リンク作成時に、UI のプリセット選択を proto の AreaPresetSelection へ変換する.
 *
 * デフォルト値をここで独立に再計算せず、UI（Select / パレット）と同じ
 * `resolveInitialPresetForArea` を共有する。これにより「画面に表示していた選択」と
 * 「共有リンクに保存される選択」が食い違わないことを保証する.
 *
 * **表示経路（`resolveEffectivePresetId`）とは意図的に正規化の扱いが違う**:
 * 表示経路は「実在しない preset ID（削除済み / presets 未ロード）」を 'all' に正規化するが、
 * 永続化経路である本関数は **正規化しない**（`resolveEffectivePresetId` を使わない）. 理由:
 * - 共有リンクのプリセット選択は「作成時点で固定される不変スナップショット」（SSOT:
 *   doc/architecture/ai-color-presets.md）。presets が未ロードのまま作成ボタンを押した瞬間に
 *   全ベースエリアが "__ALL__" で焼き付き、施主が選べる色を恒久的に広げてしまう
 * - 削除済み preset ID を保存しても、サーバ側に「存在しない / 削除済み preset ID → 全色
 *   フォールバック」の契約（SSOT の「①エリア別色候補」節）があるため、"__ALL__" を
 *   書き込むより安全側に倒れる
 * この非対称は仕様であり、「不整合」として揃えないこと.
 *
 * proto 側のキー体系（doc/architecture/ai-color-presets.md の契約）:
 * - ベースエリア（BASE_AREAS 全件）: canonical 英語名キーで必ず埋める
 * - カスタムエリア（subAreaCount 件）: bare index 文字列キーで必ず埋める
 * - UI の 'all' センチネルは proto の "__ALL__" へ変換
 */
export const buildAreaPresetSelection = (
  userSelectedPresetByArea: Record<string, string | typeof ALL_COLORS_SENTINEL>,
  baseAreaDefaultPresetIds: Record<string, string> | undefined,
  subAreaCount: number,
): { baseAreas: Record<string, string>; customAreas: Record<string, string> } => {
  const toShareValue = (editingArea: string): string => {
    const candidate =
      userSelectedPresetByArea[editingArea] ??
      resolveInitialPresetForArea(editingArea, baseAreaDefaultPresetIds)
    return candidate === ALL_COLORS_SENTINEL ? ALL_COLORS_SHARE_SENTINEL : candidate
  }

  const baseAreas: Record<string, string> = {}
  for (const areaLabel of BASE_AREAS) {
    const canonical = AREA_NAME_MAP[areaLabel] ?? areaLabel
    baseAreas[canonical] = toShareValue(areaLabel)
  }

  const customAreas: Record<string, string> = {}
  for (let idx = 0; idx < subAreaCount; idx += 1) {
    customAreas[String(idx)] = toShareValue(`subarea-${idx}`)
  }

  return { baseAreas, customAreas }
}
